<template>
  <modal
    name="zone-vehicles-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '85%'"
    :scrollable="true"
    :adaptive="true"
    :maxHeight="900"
  >
    <!-- Modal Header -->
    <div
      class="pb-3 pt-4 px-6 border-b border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 flex items-center justify-between sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-sm"
    >
      <div class="flex items-center space-x-3">
        <i class="fas fa-truck text-blue-600 dark:text-blue-400"></i>
        <h4 class="text-xl font-semibold">Araç Listesi</h4>
        <span class="text-sm text-gray-500 dark:text-gray-400">
          {{ filteredList.length > 0 ? `(${filteredList.length} araç)` : '' }}
        </span>
      </div>
      <button
        type="button"
        class="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        @click="$modal.hide('zone-vehicles-modal')"
        aria-label="Kapat"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isLoading" class="w-full h-96 flex flex-col items-center justify-center">
      <i class="fas fa-truck-loading fa-3x text-blue-500 dark:text-blue-400 mb-4 animate-pulse"></i>
      <p class="text-gray-600 dark:text-gray-300 animate-pulse">Araçlar yükleniyor...</p>
    </div>

    <!-- Modal Content -->
    <div v-if="!isLoading" class="pb-6 px-6 pt-4">
      
      <!-- Arama ve Filtreleme -->
      <div class="mb-4">
        <div class="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
          <!-- Arama Kutusu -->
          <div class="relative flex-grow">
            <div class="flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 shadow-sm">
              <i class="fas fa-search text-gray-400 dark:text-gray-300 mr-2"></i>
              <input 
                type="text" 
                v-model="search" 
                @input="applyFilters" 
                placeholder="Araç plakasına göre ara..." 
                class="w-full border-none focus:ring-0 bg-transparent text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
              />
              <button 
                v-if="search" 
                @click="clearSearch" 
                class="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <!-- Araç Türü Filtresi -->
          <div class="flex-shrink-0">
            <select 
              v-model="vehicleTypeFilter" 
              @change="applyFilters"
              class="block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            >
              <option value="all">Tüm Araç Tipleri</option>
              <option value="kirkayak">Kırkayak</option>
              <option value="cekici">Çekici</option>
            </select>
          </div>
          
          <!-- Yenile Butonu -->
          <button 
            @click="fetchVehicles" 
            class="flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md transition-colors shadow-sm"
          >
            <i class="fas fa-sync-alt mr-2"></i>
            Yenile
          </button>
        </div>
        
        <!-- Filtre bilgisi -->
        <div v-if="vehicleTypeFilter !== 'all'" class="mt-2 flex items-center">
          <span class="text-sm text-gray-600 dark:text-gray-400">
            <i class="fas fa-filter mr-1"></i>
            Filtre: 
            <span class="font-medium text-blue-600 dark:text-blue-400">
              {{ vehicleTypeFilter === 'kirkayak' ? 'Kırkayak' : 'Çekici' }}
            </span>
            <button 
              @click="clearFilters" 
              class="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Filtreleri temizle"
            >
              <i class="fas fa-times-circle"></i>
            </button>
          </span>
        </div>
      </div>
      
      <!-- Table Container with better styling -->
      <div class="overflow-hidden border-gray-200 dark:border-gray-700">
        <LyrausTable
          :searchBar="false"
          :data="getAvailableVehicles"
          :columns="columns"
          :alternateRowBg="['white', 'gray-50']"
          :containerClass="'w-full'"
          :innerThClass="'py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700'"
          :innerTrClass="'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'"
          :innerTdClass="'py-2 px-3'"
          :tdClass="'text-sm p-1 whitespace-normal'"
          :totalCount="filteredList.length" 
          :batchOperations="true"
        >
          <template v-slot:batchOperations>
            <div class="w-full ml-2 mb-4 flex items-center justify-start">
              <async-btn
                icon="fas fa-file-excel"
                text="Excel Çıktısı Al"
                loadTitle="Yükleniyor"
                :loadState="load"
                class="bg-green-600 hover:bg-green-700 transition-colors shadow-sm"
                size="py-2 px-3"
                type="button"
                @click="excelExport()"
              />
            </div>
          </template>

          <template v-slot:rowButtons="{ item, columnKey }">
            <!-- Vehicle Type Display -->
            <div v-if="columnKey === 'capacity'" class="flex items-center w-24" >
              <span 
                v-if="item.vehicleCapacity >= 18000 && item.vehicleCapacity <= 22000"
                style="background-color: #f7e6c6;"
                class="px-2 py-1 rounded-full text-xs font-medium  text-yellow-600 dark:bg-blue-900 dark:text-blue-200">
                <i class="fas fa-truck-moving mr-1"></i>
                Kırkayak
              </span>
              <span 
                v-else
                class="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                <i class="fas fa-truck mr-1"></i>
                Çekici
              </span>
            </div>

            <!-- Action Buttons -->
            <div v-if="columnKey === 'actions'" class="flex items-center space-x-2">
              <button 
                v-if="!item.isFrozen" 
                @click="freezeVehicle(item)" 
                class="px-3 py-1.5 text-xs font-medium bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="loading && currentVehicle === item.vehicle"
                title="Aracı dondur"
              >
                <i v-if="loading && currentVehicle === item.vehicle" class="fas fa-spinner fa-spin mr-1"></i>
                <i v-else class="fas fa-snowflake mr-1"></i>
                Dondur
              </button>
              <button 
                v-else 
                @click="unfreezeVehicle(item)" 
                class="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="loading && currentVehicle === item.vehicle"
                title="Dondurma işlemini kaldır"
              >
                <i v-if="loading && currentVehicle === item.vehicle" class="fas fa-spinner fa-spin mr-1"></i>
                <i v-else class="fas fa-fire mr-1"></i>
                Çöz
              </button>
            </div>
          </template>
        </LyrausTable>
      </div>

      <!-- Data count info -->
      <div v-if="filteredList.length > 0 && !isLoading" class="mt-4 text-right text-sm text-gray-500 dark:text-gray-400">
        Toplam: {{ filteredList.length }} araç
      </div>
    </div>
  </modal>
</template>

<script>
import AsyncBtn from '../../../../components/general/asyncBtn.vue';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import { routeZone } from "@/networking/urlmanager";
import moment from 'moment';
export default {
  name: "ZoneCreationModal",
  props:["detail"],
  components: {
    AsyncBtn
  },
  mounted() {
    // Modal açıldığında verileri otomatik olarak yükle
    if (this.detail && this.detail.id) {
      this.fetchVehicles();
    }
    
    // Ekran boyutu değişikliklerini takip et
    window.addEventListener('resize', this.handleResize);
  },
  
  beforeDestroy() {
    // Etkinlik dinleyicisini kaldır
    window.removeEventListener('resize', this.handleResize);
  },
  data() {
    return {
      isLoading: true, // Başlangıçta yükleniyor durumuna ayarla
      loading: false,
      currentVehicle: null,
      originalList: [], // Boş liste ile başla
      filteredList: [], // Boş liste ile başla
      search: "", // Arama terimi için value
      vehicleTypeFilter: "all", // Araç türü filtresi: all, kirkayak, cekici
      load: false, // Excel yükleme durumu
      columns:[
      {
          key:"rankInPolygon",
          label:"Sıra",
        },
        {
          key:"vehicle",
          label:"Araç Plakası",
        },
        {
          key:"polygonScore",
          label:"Bekleme Skoru",
        },
        {
          key:"entryTime",
          label:"Giriş Tarihi",
        },
        {
          key:"capacity",
          label:"Araç Türü",
        },
       
        {
          key:"haveType",
          label:"Varlık Türü",
        },
        {
          key:"lastProduct",
          label:"Son Yük"
        },
        {
          key:"dorseType",
          label:"Dorse Türü",
        },
        {
          key:"isFrozenLabel",
          label:"Durum",
          center: true,
        },
        {
          key:"actions",
          label:"İşlemler",
          center: true,
        },
      ]
    };
  },
  watch: {
    // Poligon değiştiğinde (detail) verileri yeniden yükle
    detail(newVal) {
      if (newVal && newVal.id) {
        this.fetchVehicles();
      }
    }
  },
  computed:{
    isMobile() {
      return window.innerWidth < 768;
    },
    getAvailableVehicles(){
      return this.filteredList.sort((a,b)=>a.rankInPolygon-b.rankInPolygon).map(item=>{
        return {
          ...item,
          rankInPolygon:item.polygonScore=='İşi var' ? '-' : item.rankInPolygon,
          entryTime:moment(item.entryTime).format("DD MMMM YYYY HH:mm"),
        }
      })
    }
  },
  methods: {
    // Ekran boyutu değiştirildiğinde çağrılır
    handleResize() {
      // Vue'nun reaktif özelliklerini kullandığımız için herhangi bir şey yapmamıza gerek yok
      // isMobile computed property'si otomatik olarak güncellenir
    },
    
    async fetchVehicles() {
      this.isLoading = true;
      try {
        const response = await axios.get(routeZone.getVehicles, {
          params: {
            polygonId: this.detail?.id
          },
          headers: {
            Authorization: "Bearer " + this.$store.state.userData.token,
          },
        });

        if (response.data && response.data.code === 200) {
          this.originalList = response.data.data || [];

          // Filtreleri uygula (eğer varsayılan değerlerde değilse)
          this.applyFilters();
        } else {
          this.$swal.fire({
            icon: "error",
            title: "Hata",
            text: "Araç listesi alınırken bir hata oluştu",
            confirmButtonText: "Tamam"
          });
        }
      } catch (error) {
        console.error("Araç listesi getirme hatası:", error);
        this.$swal.fire({
          icon: "error",
          title: "Hata",
          text: "Araç listesi alınırken bir hata oluştu",
          confirmButtonText: "Tamam"
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    // Tüm filtreleri uygula (hem arama hem araç tipi)
    applyFilters() {
      // Önce orijinal listeyi al
      let result = [...this.originalList];
      
      // Arama filtresini uygula
      if (this.search) {
        const searchTerm = this.search.toLowerCase();
        result = result.filter(item => {
          return (
            (item.vehicle && item.vehicle.toLowerCase().includes(searchTerm)) ||
            (item.driverName && item.driverName.toLowerCase().includes(searchTerm)) ||
            (item.dorseType && item.dorseType.toLowerCase().includes(searchTerm)) ||
            (item.lastProduct && item.lastProduct.toLowerCase().includes(searchTerm))
          );
        });
      }
      
      // Araç tipi filtresini uygula
      if (this.vehicleTypeFilter !== 'all') {
        result = result.filter(item => {
          const isKirkayak = item.vehicleCapacity >= 18000 && item.vehicleCapacity <= 22000;
          
          if (this.vehicleTypeFilter === 'kirkayak') {
            return isKirkayak;
          } else if (this.vehicleTypeFilter === 'cekici') {
            return !isKirkayak;
          }
          
          // varsayılan olarak tüm araçları göster
          return true;
        });
      }
      
      // Sonuçları filtrelenmiş listeye ata
      this.filteredList = result;
    },
    
    // Arama kutusunu temizleme
    clearSearch() {
      this.search = '';
      this.applyFilters();
    },

    // Tüm filtreleri temizle
    clearFilters() {
      this.search = '';
      this.vehicleTypeFilter = 'all';
      this.filteredList = [...this.originalList];
    },
    
    async freezeVehicle(item) {
      // Aracı dondurma işlemi
      this.loading = true;
      this.currentVehicle = item.vehicle;

      try {
        const response = await axios.post(
          routeZone.freezeVehicle,
          {
            vehiclePlate: item.vehicle,
            polygonId: this.detail?.id
          },
          {
            headers: {
              Authorization: "Bearer " + this.$store.state.userData.token,
            },
          }
        );

        if (response.data.code === 200) {
          // UI'da aracı dondurulmuş olarak işaretleyelim
          const updatedList = this.filteredList.map(vehicle => {
            if (vehicle.vehicle === item.vehicle) {
              return { ...vehicle, isFrozen: true };
            }
            return vehicle;
          });
          this.filteredList = updatedList;
          this.originalList = this.originalList.map(vehicle => {
            if (vehicle.vehicle === item.vehicle) {
              return { ...vehicle, isFrozen: true };
            }
            return vehicle;
          });
          
          this.$swal.fire({
            icon: "success",
            title: "Başarılı",
            text: "Araç başarıyla donduruldu",
            confirmButtonText: "Tamam"
          });
          
          // Yenileme: Listeyi güncellemek için
          await this.fetchVehicles();
        }
      } catch (error) {
        console.error("Araç dondurma hatası:", error);
        this.$swal.fire({
          icon: "error",
          title: "Hata",
          text: "Araç dondurulurken bir hata oluştu",
          confirmButtonText: "Tamam"
        });
      } finally {
        this.loading = false;
        this.currentVehicle = null;
      }
    },
    
    async unfreezeVehicle(item) {
      // Aracın dondurma durumunu kaldırma işlemi
      this.loading = true;
      this.currentVehicle = item.vehicle;
      try {
        const response = await axios.post(
          routeZone.unfreezeVehicle,
          {
            vehiclePlate: item.vehicle,
            polygonId: this.detail?.id
          },
          {
            headers: {
              Authorization: "Bearer " + this.$store.state.userData.token,
            },
          }
        );

        if (response.data.code === 200) {
          // UI'da aracın dondurulma durumunu kaldıralım
          const updatedList = this.filteredList.map(vehicle => {
            if (vehicle.vehicle === item.vehicle) {
              return { ...vehicle, isFrozen: false };
            }
            return vehicle;
          });
          this.filteredList = updatedList;
          this.originalList = this.originalList.map(vehicle => {
            if (vehicle.vehicle === item.vehicle) {
              return { ...vehicle, isFrozen: false };
            }
            return vehicle;
          });
          
          this.$swal.fire({
            icon: "success",
            title: "Başarılı",
            text: "Aracın dondurulma durumu başarıyla kaldırıldı",
            confirmButtonText: "Tamam"
          });
          
          // Yenileme: Listeyi güncellemek için
          await this.fetchVehicles();
        }
      } catch (error) {
        console.error("Araç dondurma kaldırma hatası:", error);
        this.$swal.fire({
          icon: "error",
          title: "Hata",
          text: "Aracın dondurulma durumu kaldırılırken bir hata oluştu",
          confirmButtonText: "Tamam"
        });
      } finally {
        this.loading = false;
        this.currentVehicle = null;
      }
    },
    
    async excelExport() {
      this.load = true;
      try {
        const workbook = XLSX.utils.book_new();

        const headers = {
          vehicle: "Araç Plakası",
          polygonScore: "Bekleme Skoru",
          entryTime: "Giriş Tarihi",
          capacity: "Araç Türü",
          dorseType: "Dorse Türü",
          haveType: "Varlık Türü",
          lastProduct: "Son Yük",
          isFrozen: "Durum"
        };


        const excelData = this.filteredList.map((item) => ({
          vehicle: item.vehicle,
          polygonScore: item.polygonScore,
          entryTime: item.entryTime,
          capacity: item.capacity,
          dorseType: item.dorseType,
          haveType: item.haveType,
          lastProduct: item.lastProduct,
          isFrozen: item.isFrozenLabel,
        }));


        if (!excelData || !Array.isArray(excelData)) {
          throw new Error("Invalid data received");
        }

        // Create worksheet data with headers and rows
        const worksheet = XLSX.utils.aoa_to_sheet([
          Object.values(headers), // Headers row
          ...excelData.map((item) => [
            item.vehicle || "",
            item.polygonScore || "",
            item.entryTime || "",
            (item.capacity>=18000 && item.capacity <=22000)?"Kırkayak":"Çekici" || "",
            item.dorseType,
            item.haveType,
            item.lastProduct,
            item.isFrozen,
          ]),
        ]);

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, "vehicleScoresRaports");

        // Create and save the file
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const data = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(data, "vehicle-scores.xlsx");
        
        this.$toast.success("Excel dosyası başarıyla oluşturuldu", {
          position: "top-right",
          timeout: 3000
        });
      } catch (error) {
        console.error("Excel export failed:", error);
        this.$toast.error("Excel dosyası oluşturulamadı", {
          position: "top-right",
          timeout: 3000
        });
      } finally {
        this.load = false;
      }
    },
  }
};
</script>